<template>
  <v-dialog
    :model-value="modelValue"
    max-width="920"
    scrollable
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="labor-ratio-modal" data-tour="bb-test-labor-dialog">
      <div class="lrm-header">
        <div class="lrm-header-icon">
          <v-icon size="22" color="white">mdi-percent-outline</v-icon>
        </div>
        <div class="lrm-header-text">
          <h3>인건비비율 계산 내역</h3>
          <p>
            {{ year }}년 {{ cumulativeLabel || '1월~해당월 누적' }}
            <template v-if="bizGubun"> · {{ bizGubun }}</template>
          </p>
        </div>
        <button type="button" class="lrm-close" @click="emit('update:modelValue', false)">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>

      <div class="lrm-body">
        <div v-if="loading" class="lrm-state">
          <v-progress-circular indeterminate size="28" width="2" color="#1e3a5f" />
          <span>불러오는 중...</span>
        </div>
        <div v-else-if="error" class="lrm-state is-error">
          <p>{{ error }}</p>
          <button type="button" class="lrm-retry" @click="load">다시 시도</button>
        </div>
        <template v-else-if="detail">
          <!-- 요약 -->
          <section class="lrm-summary" :class="detail.met ? 'is-ok' : (detail.income?.total > 0 ? 'is-warn' : 'is-empty')">
            <div class="lrm-summary-main">
              <div class="lrm-ratio-block">
                <span class="lrm-ratio-label">실제 비율</span>
                <strong class="lrm-ratio-value">
                  {{ detail.income?.total > 0 ? `${detail.ratio}%` : '-' }}
                </strong>
              </div>
              <div class="lrm-vs">÷</div>
              <div class="lrm-req-block">
                <span class="lrm-ratio-label">기준</span>
                <strong class="lrm-req-value">{{ detail.requiredPercent }}%</strong>
              </div>
              <span class="lrm-verdict" :class="detail.met ? 'ok' : 'warn'">
                {{ detail.income?.total > 0 ? (detail.met ? '충족' : '미충족') : '계산불가' }}
              </span>
            </div>
            <p class="lrm-formula">{{ detail.formula }}</p>
            <div class="lrm-calc-row">
              <span>인건비지출 {{ formatWon(detail.expense?.total) }}</span>
              <span class="lrm-calc-sep">÷</span>
              <span>인건비수입 {{ formatWon(detail.income?.total) }}</span>
              <span class="lrm-calc-sep">×</span>
              <span>100</span>
              <span class="lrm-calc-eq">=</span>
              <strong>{{ detail.income?.total > 0 ? `${detail.ratio}%` : '-' }}</strong>
            </div>
            <p v-if="detail.income?.total > 0 && !detail.met" class="lrm-need">
              기준 충족까지 약 {{ formatWon(detail.needAmount) }} 추가 지출 필요
            </p>
          </section>

          <!-- 월별 누적 -->
          <section class="lrm-section">
            <div class="lrm-section-head">
              <h4>월별 · 누적 추이</h4>
              <span class="lrm-section-hint">1월부터 {{ cumulativeTo }}월까지</span>
            </div>
            <div class="lrm-table-wrap">
              <table class="lrm-table">
                <thead>
                  <tr>
                    <th>월</th>
                    <th class="num">당월 수입</th>
                    <th class="num">당월 지출</th>
                    <th class="num">누적 수입</th>
                    <th class="num">누적 지출</th>
                    <th class="num">누적 비율</th>
                    <th>판정</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in monthlyRows"
                    :key="row.month"
                    :class="{ current: row.month === cumulativeTo }"
                  >
                    <td>{{ row.label }}</td>
                    <td class="num">{{ formatWon(row.income) }}</td>
                    <td class="num">{{ formatWon(row.expense) }}</td>
                    <td class="num">{{ formatWon(row.cumIncome) }}</td>
                    <td class="num">{{ formatWon(row.cumExpense) }}</td>
                    <td class="num" :class="row.cumRatio == null ? '' : (row.cumMet ? 'is-ok' : 'is-warn')">
                      {{ row.cumRatio == null ? '-' : `${row.cumRatio}%` }}
                    </td>
                    <td>
                      <span
                        v-if="row.cumRatio != null"
                        class="lrm-chip"
                        :class="row.cumMet ? 'ok' : 'warn'"
                      >{{ row.cumMet ? '충족' : '미충족' }}</span>
                      <span v-else class="lrm-chip muted">-</span>
                    </td>
                  </tr>
                  <tr v-if="!monthlyRows.length">
                    <td colspan="7" class="empty">월별 데이터가 없습니다</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- 수입 구성 -->
          <section class="lrm-section">
            <div class="lrm-section-head">
              <h4>인건비수입 구성</h4>
              <strong class="lrm-section-total">{{ formatWon(detail.income?.total) }}</strong>
            </div>
            <p class="lrm-note">{{ detail.income?.publicNote || detail.income?.note }}</p>
            <div class="lrm-table-wrap">
              <table class="lrm-table compact">
                <thead>
                  <tr>
                    <th>계정</th>
                    <th class="num">건수</th>
                    <th class="num">금액</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="g in (detail.income?.groups || [])" :key="'in-' + g.scode">
                    <td>{{ g.sname || g.scode }}</td>
                    <td class="num">{{ g.count }}</td>
                    <td class="num">{{ formatWon(g.amount) }}</td>
                  </tr>
                  <tr v-if="!(detail.income?.groups || []).length">
                    <td colspan="3" class="empty">해당 내역 없음</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- 지출 구성 -->
          <section class="lrm-section">
            <div class="lrm-section-head">
              <h4>인건비지출 구성</h4>
              <strong class="lrm-section-total">{{ formatWon(detail.expense?.total) }}</strong>
            </div>
            <p class="lrm-note">{{ detail.expense?.publicNote || detail.expense?.note }}</p>

            <template v-if="detail.expense?.fromWage">
              <div class="lrm-table-wrap">
                <table class="lrm-table compact">
                  <thead>
                    <tr>
                      <th>연월</th>
                      <th class="num">인건비</th>
                      <th class="num">인건비가산</th>
                      <th class="num">합계</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(w, idx) in (detail.expense?.wageRows || [])" :key="'w-' + idx">
                      <td>{{ w.label || w.yearMonth }}</td>
                      <td class="num">{{ formatWon(w.laborCost) }}</td>
                      <td class="num">{{ formatWon(w.laborAdd) }}</td>
                      <td class="num">{{ formatWon(w.amount) }}</td>
                    </tr>
                    <tr v-if="!(detail.expense?.wageRows || []).length">
                      <td colspan="4" class="empty">임금 인건비 자료 없음</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
            <template v-else>
              <div class="lrm-table-wrap">
                <table class="lrm-table compact">
                  <thead>
                    <tr>
                      <th>계정</th>
                      <th class="num">건수</th>
                      <th class="num">금액</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="g in (detail.expense?.bookGroups || [])" :key="'out-' + g.scode">
                      <td>{{ g.sname || g.scode }}</td>
                      <td class="num">{{ g.count }}</td>
                      <td class="num">{{ formatWon(g.amount) }}</td>
                    </tr>
                    <tr v-if="!(detail.expense?.bookGroups || []).length">
                      <td colspan="3" class="empty">해당 내역 없음</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </section>
        </template>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { bankbookApi } from '@/services/api';

const props = defineProps<{
  modelValue: boolean;
  memberCode: string;
  year: string | number;
  month: string | number;
  bizGubun?: string;
  bankCode?: string | null;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const loading = ref(false);
const error = ref('');
const detail = ref<any>(null);

const cumulativeTo = computed(() =>
  Number(detail.value?.cumulativeTo || props.month) || Number(props.month) || 1
);

const cumulativeLabel = computed(() =>
  detail.value?.cumulativeLabel
  || (cumulativeTo.value <= 1 ? '1월 누적' : `1~${cumulativeTo.value}월 누적`)
);

const monthlyRows = computed(() => detail.value?.monthlyRows || []);

const formatWon = (n: number | null | undefined) => {
  const v = Number(n) || 0;
  return `${v.toLocaleString('ko-KR')}원`;
};

const load = async () => {
  if (!props.memberCode || !props.year || !props.month) return;
  loading.value = true;
  error.value = '';
  detail.value = null;
  try {
    const monthNum = parseInt(String(props.month), 10);
    const res = await bankbookApi.getAccountingAnalysisReference(
      String(props.memberCode),
      {
        year: String(props.year),
        month: String(monthNum),
        bizGubun: props.bizGubun || undefined,
        bankCode: props.bankCode || undefined,
        periodType: 'month',
        startMonth: monthNum,
        endMonth: monthNum,
      }
    );
    const data = res.data?.data;
    if (!data?.laborRatioDetail) {
      error.value = '인건비비율 상세 데이터가 없습니다.';
      return;
    }
    detail.value = data.laborRatioDetail;
  } catch (e: any) {
    console.error(e);
    error.value = e?.response?.data?.message || '인건비비율 내역을 불러오지 못했습니다.';
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.modelValue,
  (open) => {
    if (open) load();
  }
);
</script>
